import { ReactNode } from "react";
import styles from "./ErrorBanner.module.scss";

export interface ErrorBannerProps {
  /** Error title */
  title?: string;
  /** Error message */
  message: string;
  /** Optional action button */
  action?: ReactNode;
  /** Banner variant */
  variant?: "error" | "warning" | "info";
  /** Dismissible */
  onDismiss?: () => void;
  /** Additional class */
  className?: string;
}

/**
 * ErrorBanner Component
 *
 * Alert banner for displaying errors, warnings, and info messages
 *
 * @example
 * ```tsx
 * <ErrorBanner
 *   variant="error"
 *   title="Authentication Failed"
 *   message="Invalid email or password. Please try again."
 *   onDismiss={() => setError(null)}
 * />
 * ```
 */
export function ErrorBanner({
  title,
  message,
  action,
  variant = "error",
  onDismiss,
  className,
}: ErrorBannerProps) {
  const Icon =
    variant === "error"
      ? AlertCircleIcon
      : variant === "warning"
        ? AlertTriangleIcon
        : InfoIcon;

  return (
    <div
      role="alert"
      className={`${styles.banner} ${styles[variant]} ${className || ""}`}
    >
      <div className={styles.iconWrapper}>
        <Icon />
      </div>

      <div className={styles.content}>
        {title && <h4 className={styles.title}>{title}</h4>}
        <p className={styles.message}>{message}</p>
        {action && <div className={styles.action}>{action}</div>}
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={styles.dismissButton}
          aria-label="Dismiss"
        >
          <XIcon />
        </button>
      )}
    </div>
  );
}

// Icons
const AlertCircleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const XIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
